import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import {
  Table,
  Tag,
  Button,
  Steps,
  Alert,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Descriptions,
  Space,
  Row,
  Col,
  Typography,
  Spin,
  Empty,
  Dropdown,
  Tooltip,
  Divider,
} from 'antd';
import { renderToStaticMarkup } from 'react-dom/server';
import { printInIframe } from '../../utils/printIframe';
import BoutonFlottant, { STYLE_BOUTON_FLOTTANT } from '../../components/BoutonFlottant';
import { etatBoutonRc } from './etatReglementComptable';
import { netAPayer, recapCheque } from './montantsLot';
import {
  LIBELLE_STATUT_LOT, libelleStatutLot, libelleStatutPaiement,
} from './libellesStatut';
/* LA FEUILLE DOIT SUIVRE LE DOCUMENT. `renderToStaticMarkup` produit du HTML nu,
   imprime dans un document SEPARE : l'`import` de la feuille dans `OrdreDeDepense`
   alimente l'apercu a l'ecran, jamais ce document-la. Les trois impressions de cet
   ecran passaient une chaine vide - l'ordre de depense sortait donc sans en-tete et
   sans tableaux, ses classes n'etant definies nulle part. L'onglet des reglements d'un
   sinistre passait deja cette feuille ; cet ecran-ci ne l'a jamais fait. */
import quittanceCss from '../../assets/styles/quittanceIndemniteRaw';
import OrdreDeDepense from '../sinistres/components/OrdreDeDepense';
import OrdreDeDepenseLotGlobal from './OrdreDeDepenseLotGlobal';
import {
  FileTextOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileDoneOutlined,
  BankOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  UnorderedListOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import { pourcent } from '../../services/tauxRetenueService';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatMontant, formatDate } from '../../variables/constants';

import { useTranslation, Trans } from 'react-i18next';
import i18n from '../../i18n';
import { avecLibelleTraduit } from '../../i18n/libelles';

const { Option } = Select;

// `get label()` : le libelle se lit au rendu, dans la langue de l'ecran, et non au
// chargement du module.
const STATUT_LOT_DISPLAY = {
  EMIS: {
    color: 'default',
    icon: <ClockCircleOutlined />,
    get label() { return LIBELLE_STATUT_LOT.EMIS; },
  },
  REGLEMENT_TECHNIQUE_VALIDE: {
    color: 'processing',
    icon: <FileDoneOutlined />,
    get label() { return LIBELLE_STATUT_LOT.REGLEMENT_TECHNIQUE_VALIDE; },
  },
  REGLEMENT_COMPTABLE_VALIDE: {
    color: 'cyan',
    icon: <BankOutlined />,
    get label() { return LIBELLE_STATUT_LOT.REGLEMENT_COMPTABLE_VALIDE; },
  },
  PAYE: {
    color: 'success',
    icon: <CheckCircleOutlined />,
    get label() { return LIBELLE_STATUT_LOT.PAYE; },
  },
  PARTIELLEMENT_ANNULE: {
    color: 'warning',
    icon: <ExclamationCircleOutlined />,
    get label() { return LIBELLE_STATUT_LOT.PARTIELLEMENT_ANNULE; },
  },
  ANNULE: {
    color: 'error',
    icon: <ExclamationCircleOutlined />,
    get label() { return LIBELLE_STATUT_LOT.ANNULE; },
  },
};

/* LES COULEURS DES CINQ STATUTS DE `StatutPaiement`. Il en manquait deux - les deux
   etapes du circuit comptable, donc les plus fréquentes - et il y en avait une de trop,
   « VALIDE », qui n'existe dans aucun enum du serveur. */
const PAIEMENT_STATUT_COLORS = {
  EMIS: 'blue',
  REGLEMENT_TECHNIQUE_VALIDE: 'processing',
  REGLEMENT_COMPTABLE_VALIDE: 'cyan',
  PAYE: 'success',
  ANNULE: 'default',
};



const WORKFLOW_STEPS = avecLibelleTraduit([
  { key: 'EMIS' },
  { key: 'REGLEMENT_TECHNIQUE_VALIDE' },
  { key: 'REGLEMENT_COMPTABLE_VALIDE' },
  { key: 'PAYE' },
], 'reglementsExperts:detail.statutsLot', { cle: 'key', champ: 'title' });

const round2 = (n) => Math.round(n * 100) / 100;

const extractObject = (data) => data?.data ?? data ?? null;

function getStepCurrent(statut) {
  const index = WORKFLOW_STEPS.findIndex((s) => s.key === statut);
  return index >= 0 ? index : 0;
}

//  Modal saisie chèque global 
/**
 * Le recapitulatif montre avant d'enregistrer le cheque, en lignes libelle / valeur.
 *
 * <p>Le montant est mis en avant : c'est le seul chiffre que l'utilisateur peut comparer
 * au cheque qu'il tient en main, et la seule erreur qu'une relecture rattrape.
 */
function RecapCheque({ lignes }) {
  return (
    <div style={{ marginTop: 12 }}>
      {lignes.map((l) => (
        <div
          key={l.libelle}
          style={{
            display: 'flex', justifyContent: 'space-between', gap: 16,
            padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13,
          }}
        >
          <span style={{ color: '#6b7280' }}>{l.libelle}</span>
          <span style={l.estMontant
            ? { fontWeight: 700, color: '#1a6b3c', fontSize: 15 }
            : { fontWeight: 600, color: '#111827' }}>
            {l.estMontant ? formatMontant(l.valeur) : l.valeur}
          </span>
        </div>
      ))}
    </div>
  );
}

function ModalSaisirCheque({ open, onCancel, onFinish, loading, lot }) {
  const { t } = useTranslation('reglementsExperts');
  const [form] = Form.useForm();
  const [banques, setBanques] = useState([]);
  const [loadingBanques, setLoadingBanques] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingBanques(true);
    axiosInstance.get('/v1/banques')
      .then((r) => setBanques(r.data?.data || []))
      .catch(() => toast.error(i18n.t('reglementsExperts:cheque.erreurBanques')))
      .finally(() => setLoadingBanques(false));

    form.setFieldsValue({
      dateEmissionReglement: dayjs(),
      dateEmissionCheque: dayjs(),
    });
  }, [open, form]);

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      // validation échouée - antd affiche les messages inline
      return;
    }
    const corps = {
      numeroChequeEmis: values.numeroChequeEmis,
      banqueCheque: values.banqueCheque,
      dateEmissionReglement: values.dateEmissionReglement.format('YYYY-MM-DD'),
      dateEmissionCheque: values.dateEmissionCheque.format('YYYY-MM-DD'),
    };
    /* UNE RELECTURE AVANT D'ENGAGER. L'enregistrement fait avancer le lot d'une etape du
       circuit comptable, et le numero de cheque est unique - le serveur le refuse s'il
       existe deja ailleurs. Le recapitulatif montre ce qui se verifie contre le cheque
       qu'on tient en main, son montant en premier. */
    Modal.confirm({
      title: t('cheque.confirmation.titre'),
      icon: <ExclamationCircleOutlined style={{ color: '#1a6b3c' }} />,
      width: 460,
      content: <RecapCheque lignes={recapCheque(lot, corps)} />,
      okText: t('cheque.confirmation.enregistrer'),
      cancelText: t('cheque.confirmation.revenir'),
      okButtonProps: { style: { background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 } },
      cancelButtonProps: { style: { borderRadius: 8 } },
      onOk: () => onFinish(corps),
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BankOutlined style={{ color: '#1a6b3c', fontSize: 18 }} />
          <span style={{ fontWeight: 700 }}>{t('cheque.titre')}</span>
        </div>
      }
      width={540}
      footer={[
        <Button key="cancel" style={{ borderRadius: 8 }} onClick={handleCancel}>
          {t('actions.annuler')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          icon={<BankOutlined />}
          style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}
          onClick={handleSubmit}
        >
          {t('actions.enregistrer')}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('cheque.numeroCheque')}
              name="numeroChequeEmis"
              rules={[{ required: true, message: t('cheque.numeroObligatoire') }]}
            >
              <Input placeholder={t('cheque.exempleNumero')} style={{ borderRadius: 8 }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('cheque.banqueEmettrice')} name="banqueCheque">
              <Select
                showSearch
                loading={loadingBanques}
                placeholder={banques.length === 0 ? t('cheque.aucuneBanque') : t('cheque.selectionner')}
                disabled={banques.length === 0}
                filterOption={(input, opt) =>
                  String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={banques.map((b) => ({
                  value: b.nom,
                  label: b.code ? `${b.nom} (${b.code})` : b.nom,
                }))}
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('cheque.dateEmissionReglement')}
              name="dateEmissionReglement"
              rules={[{ required: true, message: t('cheque.dateObligatoire') }]}
            >
              <DatePicker
                style={{ width: '100%', borderRadius: 8 }}
                format="DD/MM/YYYY"
                disabledDate={(d) => d && d > dayjs().endOf('day')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('cheque.dateEmissionCheque')}
              name="dateEmissionCheque"
              rules={[{ required: true, message: t('cheque.dateObligatoire') }]}
            >
              <DatePicker
                style={{ width: '100%', borderRadius: 8 }}
                format="DD/MM/YYYY"
                disabledDate={(d) => d && d > dayjs().endOf('day')}
              />
            </Form.Item>
          </Col>
        </Row>

        {banques.length === 0 && (
          <Alert
            type="info"
            showIcon
            style={{ borderRadius: 8, marginTop: 4 }}
            message={t('cheque.aucuneBanqueAide')}
          />
        )}
      </Form>
    </Modal>
  );
}

//  Composant principal 
export default function LotReglementDetail() {
  const { lotTrackingId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { t } = useTranslation('reglementsExperts');

  const [chequeModalOpen, setChequeModalOpen] = useState(false);
  const [organisme, setOrganisme] = useState(null);
  /* LE BUREAU CARTE BRUNE, et non l'assureur du dossier.

     `organisme` ci-dessus est l'organisme MEMBRE du premier sinistre du lot - une
     compagnie d'assurance. `OrdreDeDepense` fait `bureau = props.bureau || organisme` :
     sans `bureau`, l'en-tete, le pied de page et les signataires de tous les ordres de
     depense d'un lot sortaient au nom de cette compagnie.

     L'onglet des reglements d'un sinistre avait deja corrige cela de son cote - « la
     quittance sortait au nom d'une compagnie », dit son commentaire. Cet ecran-ci ne
     l'avait jamais fait. */
  const [bureau, setBureau] = useState(null);
  const [saisirRcRow, setSaisirRcRow] = useState(null); // paiement object or null

  const { data, isLoading, isError } = useQuery({
    queryKey: ['lot-reglement', lotTrackingId],
    queryFn: () => axiosInstance.get(`/v1/lot-reglements/${lotTrackingId}`),
    enabled: !!lotTrackingId,
  });

  const lot = extractObject(data?.data);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['lot-reglement', lotTrackingId] });
    queryClient.invalidateQueries({ queryKey: ['lot-reglements'] });
  };

  // Fetch organisme via le premier paiement du lot
  const paiements = lot?.paiements ?? [];
  useEffect(() => {
    const firstSinistreId = lot?.paiements?.[0]?.sinistreTrackingId;
    if (!firstSinistreId) return;
    axiosInstance.get(`/v1/sinistres/${firstSinistreId}`)
      .then((res) => {
        const orgId = res.data?.data?.organismeMembreTrackingId;
        if (orgId) {
          return axiosInstance.get(`/v1/organismes/${orgId}`);
        }
        return null;
      })
      .then((res) => setOrganisme(res?.data?.data || null))
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotTrackingId, !!lot]);

  useEffect(() => {
    axiosInstance.get('/v1/organismes/type/BUREAU_NATIONAL', { skipErrorToast: true })
      // Sans bureau, le document retombe sur l'organisme du dossier : c'est l'ancien
      // comportement, pas une page blanche - un toast de plus n'aiderait personne.
      .then((res) => setBureau((res.data?.data || [])[0] || null))
      .catch(() => setBureau(null));
  }, []);

  // Vérifie qu'une URL d'image charge avec succès. Renvoie l'URL si OK, null sinon.
  const checkImageOrNull = (url) =>
    new Promise((resolve) => {
      if (!url) return resolve(null);
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => resolve(null);
      img.src = url;
    });

  // Pré-vérifie l'image backend de l'organisme. Si KO, on patch organisme pour que
  // OrdreDeDepense fallback sur le logo local au render.
  const resolvePourImpression = async (org) => {
    if (!org) return org;
    const baseUrl = (axiosInstance.defaults.baseURL || '').replace(/\/$/, '');
    const candidate = org.headerImageUrl || org.logo;
    const fullUrl = candidate && !/^(https?:|data:)/i.test(candidate) ? `${baseUrl}${candidate}` : candidate;
    const ok = await checkImageOrNull(fullUrl);
    if (ok) return org;
    return { ...org, headerImageUrl: null, logo: null };
  };

  /* LES DEUX SONT PRE-VERIFIES. `renderToStaticMarkup` serialise le document sans
     attendre les images : une URL qui ne charge pas sortirait en cadre vide. Le bureau
     fournit l'en-tete, l'organisme reste le repli - il faut donc verifier les deux, et
     non le seul organisme comme avant. */
  const resolveOrganismeForPrint = () => resolvePourImpression(organisme);
  const resolveBureauForPrint = () => resolvePourImpression(bureau);

  const printOrdreGeneral = async () => {
    const [orgPrint, bureauPrint] = await Promise.all([
      resolveOrganismeForPrint(), resolveBureauForPrint(),
    ]);
    const html = renderToStaticMarkup(
      <OrdreDeDepenseLotGlobal
        lot={lot}
        expert={{ expertNomComplet: lot?.expertNomComplet }}
        organisme={orgPrint}
        bureau={bureauPrint}
        paiements={paiements}
      />
    );
    printInIframe(html, quittanceCss, 'Lot de règlements');
  };

  const printOrdresIndividuels = async () => {
    if (!paiements.length) return;
    const [orgPrint, bureauPrint] = await Promise.all([
      resolveOrganismeForPrint(), resolveBureauForPrint(),
    ]);
    const lignesHtml = paiements.map((p, idx) =>
      renderToStaticMarkup(
        <div style={{ pageBreakAfter: 'always' }}>
          <OrdreDeDepense
            reglement={p}
            sinistre={null}
            organisme={orgPrint}
            bureau={bureauPrint}
            lotIndice={{
              numeroLot: lot?.numeroLot || lotTrackingId?.slice(0, 8).toUpperCase(),
              ligne: idx + 1,
              totalLignes: paiements.length,
            }}
          />
        </div>
      )
    ).join('');
    printInIframe(lignesHtml, quittanceCss, 'Lot de règlements');
  };

  const printOneOrdre = async (paiement, indexInLot) => {
    if (!paiement) return;
    const [orgPrint, bureauPrint] = await Promise.all([
      resolveOrganismeForPrint(), resolveBureauForPrint(),
    ]);
    const html = renderToStaticMarkup(
      <OrdreDeDepense
        reglement={paiement}
        sinistre={null}
        organisme={orgPrint}
        bureau={bureauPrint}
        lotIndice={{
          numeroLot: lot?.numeroLot || lotTrackingId?.slice(0, 8).toUpperCase(),
          ligne: indexInLot + 1,
          totalLignes: paiements.length,
        }}
      />
    );
    printInIframe(html, quittanceCss, 'Ordre de dépense');
  };

  const ordreDeDepenseMenuItems = [
    {
      key: 'general',
      label: t('documents.ordreGeneral'),
      onClick: printOrdreGeneral,
    },
    {
      key: 'individuel',
      label: t('documents.ordresIndividuels', { count: paiements.length }),
      onClick: printOrdresIndividuels,
    },
  ];

  const mutationValiderTechnique = useMutation({
    mutationFn: () =>
      axiosInstance.post(`/v1/lot-reglements/${lotTrackingId}/valider-technique`),
    onSuccess: () => {
      toast.success(t('messages.validationTechnique'));
      invalidate();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t('messages.erreurValidationTechnique'));
    },
  });

  const mutationSaisirComptable = useMutation({
    mutationFn: (body) =>
      axiosInstance.post(`/v1/lot-reglements/${lotTrackingId}/saisir-comptable`, body),
    onSuccess: () => {
      toast.success(t('messages.chequeEnregistre'));
      setChequeModalOpen(false);
      invalidate();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t('messages.erreurSaisieComptable'));
    },
  });

  /* LA VALIDATION TECHNIQUE AUSSI SE CONFIRME.

     Elle partait au clic, sans un mot. Elle fait avancer le lot ET chacun de ses
     paiements, et le serveur refuse ensuite de revenir en arriere - il exige le statut
     EMIS, qu'aucun ecran ne sait rendre. Un clic de trop se defaisait a la main, en base.

     Ce n'etait pas demande pour ce bouton-la, mais il est desormais au meme endroit, de
     la meme couleur et de la meme portee que celui du cheque : lui seul partant sans
     confirmation, c'est lui qu'on aurait clique par surprise. */
  const confirmerValidationTechnique = () => {
    Modal.confirm({
      title: t('actions.confirmationTechnique.titre'),
      icon: <ExclamationCircleOutlined style={{ color: '#1a6b3c' }} />,
      width: 460,
      content: (
        <div style={{ marginTop: 8 }}>
          <p style={{ margin: '0 0 10px', color: '#374151' }}>
            <Trans
              t={t}
              i18nKey="actions.confirmationTechnique.resume"
              count={lot.nombreReglements ?? 0}
              values={{
                expert: lot.expertNomComplet,
                montant: formatMontant(netAPayer(lot.montantTtcTotal, lot.montantTaxeTotal)),
              }}
              components={{ 1: <strong style={{ color: '#1a6b3c' }} /> }}
            />
          </p>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
            {t('actions.confirmationTechnique.irreversible')}
          </p>
        </div>
      ),
      okText: t('actions.validerTechnique'),
      cancelText: t('actions.annuler'),
      okButtonProps: { style: { background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 } },
      cancelButtonProps: { style: { borderRadius: 8 } },
      onOk: () => mutationValiderTechnique.mutateAsync(),
    });
  };

  const mutationValiderComptable = useMutation({
    mutationFn: () =>
      axiosInstance.post(`/v1/lot-reglements/${lotTrackingId}/valider-comptable`),
    onSuccess: () => {
      toast.success(t('messages.validationComptable'));
      invalidate();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t('messages.erreurValidationComptable'));
    },
  });

  const mutationSaisirRcLigne = useMutation({
    mutationFn: ({ paiementTrackingId, body }) =>
      axiosInstance.post(`/v1/paiements/${paiementTrackingId}/reglement-comptable`, body),
    onSuccess: () => {
      toast.success(t('messages.rcEnregistre'));
      setSaisirRcRow(null);
      invalidate();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t('messages.erreurRc'));
    },
  });

  if (isLoading) {
    return (
      <Layout title={t('detail.titre')}>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  if (isError || !lot) {
    return (
      <Layout title={t('detail.titre')}>
        <Alert
          type="error"
          message={t('detail.introuvable')}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/lot-reglements')} style={{ borderRadius: 8 }}>
          {t('detail.retourListe')}
        </Button>
      </Layout>
    );
  }

  const statut = lot.statut;
  const statutDisplay = STATUT_LOT_DISPLAY[statut]
    ?? { color: 'default', icon: null, label: libelleStatutLot(statut) };

  /* LA TVA EST-ELLE EN JEU DANS CE LOT ?

     Elle ne s'applique pas aux reglements aux experts - l'ecran de creation pose
     `tauxTva = 0`, et aucun paiement en base n'en porte. Les colonnes « HT » et « TVA »
     affichaient donc « 0 F » sur chaque ligne et un montant HT identique au TTC : trois
     colonnes pour un seul nombre.

     Elles ne sont pas supprimees mais conditionnees : si la decision metier change un
     jour, HT cessera d'egaler TTC et les deux colonnes reparaitront d'elles-memes. */
  const tvaEnJeu = (lot.paiements ?? []).some((p) => Number(p.montantTva || 0) !== 0);

  const paiementsColumns = [
    {
      title: t('colonnes.numeroPaiement'),
      dataIndex: 'numeroPaiement',
      key: 'numeroPaiement',
      width: 160,
      render: (v) => (
        <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600, color: '#1a6b3c' }}>{v}</span>
      ),
    },
    {
      title: t('colonnes.sinistre'),
      dataIndex: 'sinistreReference',
      key: 'sinistreReference',
      render: (v) => v ?? '-',
    },
    /* PAS DE COLONNE « BENEFICIAIRE ». Un lot est fait pour UN expert : la colonne
       repetait le meme nom sur chaque ligne, alors qu'il est deja ecrit deux fois plus
       haut - dans le bandeau de l'en-tete et dans les informations du lot. */
    ...(tvaEnJeu ? [
      {
        title: t('colonnes.ht'),
        dataIndex: 'montantTtc',
        key: 'ht',
        align: 'right',
        width: 120,
        render: (ttc, record) =>
          formatMontant(round2((ttc || 0) - (record.montantTva || 0))),
      },
      {
        title: t('colonnes.tva'),
        dataIndex: 'montantTva',
        key: 'tva',
        align: 'right',
        width: 120,
        render: (v) => formatMontant(v),
      },
    ] : []),
    {
      // « Montant » quand la TVA n'est pas en jeu : « TTC » n'oppose alors rien a rien.
      title: tvaEnJeu ? t('colonnes.ttc') : t('colonnes.montant'),
      dataIndex: 'montantTtc',
      key: 'ttc',
      align: 'right',
      width: 130,
      render: (v) => (
        <span style={{ fontWeight: 700, color: '#000000' }}>{formatMontant(v)}</span>
      ),
    },
    {
      title: t('colonnes.retenue'),
      dataIndex: 'montantTaxe',
      key: 'taxe',
      align: 'right',
      width: 120,
      render: (v) => formatMontant(v),
    },
    {
      /* LE NET, QUI MANQUAIT. Le tableau donnait le montant et la retenue, mais pas la
         difference - c'est-a-dire le seul chiffre que l'expert touche, et celui du
         cheque. Ce n'est pas une redondance : aucune colonne ne le portait. */
      title: t('colonnes.netAPayer'),
      key: 'net',
      align: 'right',
      width: 130,
      render: (_, r) => (
        <span style={{ fontWeight: 700, color: '#1a6b3c' }}>
          {formatMontant(netAPayer(r.montantTtc, r.montantTaxe))}
        </span>
      ),
    },
    {
      title: t('colonnes.statut'),
      dataIndex: 'statut',
      key: 'statut',
      width: 110,
      render: (v) => (
        <Tag color={PAIEMENT_STATUT_COLORS[v] ?? 'default'} style={{ borderRadius: 20 }}>
          {libelleStatutPaiement(v)}
        </Tag>
      ),
    },
    {
      title: t('colonnes.actions'),
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record, index) => {
        /* LA MEME REGLE QUE LE SERVEUR, et dans le meme sens : une liste blanche. Le
           bouton etait actif sur tout statut sauf deux - un lot en « EMIS » ouvrait donc
           la modale, faisait saisir un numero de cheque et des dates, puis echouait a
           l'envoi. La saisie etait perdue, et rien n'avait prevenu. */
        const rc = etatBoutonRc(record.statut);
        return (
          <Space size="small">
            <Tooltip title={t('colonnes.imprimerOrdre')}>
              <Button
                size="small"
                icon={<PrinterOutlined />}
                onClick={() => printOneOrdre(record, index)}
              />
            </Tooltip>
            <Tooltip title={rc.raison}>
              <Button
                size="small"
                icon={<BankOutlined />}
                disabled={!rc.autorise}
                onClick={() => setSaisirRcRow(record)}
              >
                RC
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Layout title={t('detail.titreLayout', { id: lotTrackingId?.slice(0, 8).toUpperCase() })}>

      {/* En-tête page - bandeau vert application */}
      <div
        className="cb-card"
        style={{
          marginBottom: 16,
          padding: '18px 24px',
          border: 'none',
          background: 'linear-gradient(135deg, var(--cb-green), var(--cb-green-dark))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FileTextOutlined style={{ fontSize: 22, color: '#fff' }} />
          </div>
          <div>
            <h1 className="cb-page-title" style={{ color: '#fff', margin: 0 }}>
              {t('detail.titreLot', { id: lotTrackingId?.slice(0, 8).toUpperCase() })}
            </h1>
            <p className="cb-page-sub" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.85)', margin: '4px 0 0' }}>
              {t('detail.expertNom', { nom: lot.expertNomComplet })}
              <Tag
                color={statutDisplay.color}
                icon={statutDisplay.icon}
                style={{ marginLeft: 4, borderRadius: 20 }}
              >
                {statutDisplay.label}
              </Tag>
            </p>
          </div>
        </div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/lot-reglements')}
          style={{ borderRadius: 8, background: '#fff', borderColor: '#fff', color: 'var(--cb-green)' }}
        >
          {t('detail.retourListeCourt')}
        </Button>
      </div>

      {/* Alerte partiel */}
      {statut === 'PARTIELLEMENT_ANNULE' && (
        <Alert
          type="warning"
          message={t('detail.partiellementAnnule')}
          showIcon
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      {/* Workflow Steps */}
      <div className="cb-card" style={{ marginBottom: 16, background: '#f4f6f5' }}>
        <Steps
          className="cb-steps-green"
          current={getStepCurrent(statut)}
          size="small"
          items={WORKFLOW_STEPS.map((s) => ({ title: s.title }))}
          style={{ marginBottom: 0 }}
        />
      </div>

      {/* UNE SEULE CARD POUR LE LOT ET SES PAIEMENTS.

          Les deux vivaient dans deux cards empilees : on relisait les totaux du lot en
          remontant, puis on redescendait au tableau pour les retrouver ligne a ligne. Ce
          sont deux lectures du meme objet, et elles tiennent desormais ensemble, separees
          par un filet plutot que par un blanc entre deux cadres. */}
      <div className="cb-card" style={{ marginBottom: 16 }}>
        <div className="cb-card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <InfoCircleOutlined style={{ color: '#1a6b3c', fontSize: 16 }} />
            <span className="cb-card-title">{t('detail.informations')}</span>
          </div>
        </div>
        {/* NI « ID LOT », NI « EXPERT », NI « STATUT ». Les trois sont ecrits dans le
            bandeau vert juste au-dessus - le titre porte l'identifiant, le sous-titre
            l'expert, et la pastille le statut. Les redire ici occupait trois cases sur
            quatre de la premiere ligne, et repoussait les montants a la ligne suivante. */}
        <Descriptions size="small" column={{ xs: 2, sm: 3, md: 4 }} bordered>
          <Descriptions.Item label={t('detail.tauxRetenue')}>
            {/* Taux appliqué au lot, figé à sa création. */}
            {lot.tauxRetenueValeur != null ? pourcent(lot.tauxRetenueValeur) : (lot.tauxRetenue ?? '-')}
          </Descriptions.Item>
          <Descriptions.Item label={t('detail.nbReglements')}>
            {lot.nombreReglements ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('detail.totalTtc')}>
            <span style={{ fontWeight: 700, color: '#000000' }}>
              {formatMontant(lot.montantTtcTotal)}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label={t('detail.totalTva')}>
            {formatMontant(lot.montantTvaTotal)}
          </Descriptions.Item>
          <Descriptions.Item label={t('detail.taxeRetenue')}>
            {formatMontant(lot.montantTaxeTotal)}
          </Descriptions.Item>
          {lot.numeroChequeGlobal && (
            <Descriptions.Item label={t('detail.chequeGlobal')}>
              <span style={{ fontFamily: 'var(--font-main)' }}>
                {lot.numeroChequeGlobal}
              </span>
            </Descriptions.Item>
          )}
          {lot.banqueCheque && (
            <Descriptions.Item label={t('detail.banque')}>
              {lot.banqueCheque}
            </Descriptions.Item>
          )}
          {lot.dateEmissionReglement && (
            <Descriptions.Item label={t('detail.dateEmissionReglement')}>
              {formatDate(lot.dateEmissionReglement)}
            </Descriptions.Item>
          )}
          {lot.dateEmissionCheque && (
            <Descriptions.Item label={t('detail.dateEmissionCheque')}>
              {formatDate(lot.dateEmissionCheque)}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t('detail.creeLe')}>
            {formatDate(lot.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label={t('detail.creePar')}>
            {lot.createdBy ?? '-'}
          </Descriptions.Item>
        </Descriptions>

        {/* LE FILET QUI SEPARE LES DEUX LECTURES. Une card, deux sections : le totaux du
            lot au-dessus, son detail ligne a ligne en dessous. */}
        <Divider style={{ margin: '20px 0 0' }} />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            padding: '16px 0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UnorderedListOutlined style={{ color: '#1a6b3c', fontSize: 16 }} />
            <span className="cb-card-title">
              {t('detail.paiementsTitre', { n: lot.paiements?.length ?? 0 })}
            </span>
          </div>

          {/* LA VALIDATION TECHNIQUE, AU-DESSUS DE CE QU'ELLE VALIDE. Elle vivait dans
              une card « Actions » tout en bas de l'ecran : on validait des montants
              apres les avoir perdus de vue. Elle est ici, a droite du titre du tableau
              qu'elle porte. */}
          {statut === 'EMIS' && (
            <Space>
              <Typography.Text style={{ color: '#6b7280', fontSize: 12 }}>
                {t('actions.verifierMontants')}
              </Typography.Text>
              <Button
                type="primary"
                loading={mutationValiderTechnique.isPending}
                onClick={confirmerValidationTechnique}
                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}
              >
                {t('actions.validerTechnique')}
              </Button>
            </Space>
          )}

          {/* L'ETAPE SUIVANTE, AU MEME ENDROIT. Les deux boutons ne paraissent jamais
              ensemble - le statut en designe un seul - et le coin haut droit du tableau
              porte donc toujours l'action du moment, au-dessus des montants qu'elle
              engage. Le cheque vivait auparavant dans une card « Actions » tout en bas. */}
          {statut === 'REGLEMENT_TECHNIQUE_VALIDE' && (
            <Space>
              <Typography.Text style={{ color: '#6b7280', fontSize: 12 }}>
                <Trans
                  t={t}
                  i18nKey="actions.netAPayer"
                  values={{ montant: formatMontant(netAPayer(lot.montantTtcTotal, lot.montantTaxeTotal)) }}
                  components={{ 1: <strong style={{ color: '#1a6b3c' }} /> }}
                />
              </Typography.Text>
              <Button
                type="primary"
                icon={<BankOutlined />}
                onClick={() => setChequeModalOpen(true)}
                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}
              >
                {t('actions.saisirCheque')}
              </Button>
            </Space>
          )}
        </div>
        <Table
          className="cb-table"
          rowKey="paiementTrackingId"
          columns={paiementsColumns}
          dataSource={lot.paiements ?? []}
          pagination={false}
          size="small"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ color: '#6b7280' }}>{t('detail.aucunPaiement')}</span>
                }
              />
            ),
          }}
          scroll={{ x: 'max-content' }}
          rowClassName={(record) =>
            record.statut === 'ANNULE' ? 'row-paiement-annule' : ''
          }
        />
        <style>{`
          .row-paiement-annule td {
            background: #fee2e2 !important;
            color: #9ca3af;
            text-decoration: line-through;
          }
        `}</style>
      </div>

      {/* LA CARD « ACTIONS » NE PORTE PLUS LA VALIDATION TECHNIQUE : elle est passee en
          haut a droite du tableau des paiements, au-dessus des montants qu'elle valide.
          Les deux etapes suivantes restent ici - elles ne portent pas sur les lignes mais
          sur le cheque global du lot. */}
      {/* LA CARD « ACTIONS » NE GARDE QUE LA DERNIERE ETAPE. La validation technique et la
          saisie du cheque sont passees en haut a droite du tableau des paiements, au-dessus
          des montants qu'elles engagent. */}
      {statut === 'REGLEMENT_COMPTABLE_VALIDE' && (
          <div className="cb-card">
            <div className="cb-card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: '#1a6b3c', fontSize: 16 }} />
                <span className="cb-card-title">{t('actions.titre')}</span>
              </div>
            </div>

            {statut === 'REGLEMENT_COMPTABLE_VALIDE' && (
              <Space direction="vertical">
                <Typography.Text style={{ color: '#6b7280' }}>
                  {t('actions.attenteComptable')}
                </Typography.Text>
                <Button
                  type="primary"
                  loading={mutationValiderComptable.isPending}
                  onClick={() => mutationValiderComptable.mutate()}
                  style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}
                >
                  {t('actions.validerComptable')}
                </Button>
              </Space>
            )}
          </div>
        )}

      {/* EN BAS A DROITE, comme les boutons d'export des autres ecrans. `BoutonFlottant`
          pose le coin ET la reserve de place en bas de page. Il remplace une card
          « Documents » qui n'avait qu'un bouton pour contenu, tout en bas de l'ecran. */}
      {paiements.length > 0 && (
        <BoutonFlottant>
          <Dropdown menu={{ items: ordreDeDepenseMenuItems }} placement="topRight">
            <Button
              icon={<PrinterOutlined />}
              style={{ ...STYLE_BOUTON_FLOTTANT, background: '#1a6b3c', color: '#fff' }}
            >
              {t('documents.imprimerOrdres')}
            </Button>
          </Dropdown>
        </BoutonFlottant>
      )}

      {/* Modal saisie chèque */}
      <ModalSaisirCheque
        lot={lot}
        open={chequeModalOpen}
        onCancel={() => setChequeModalOpen(false)}
        loading={mutationSaisirComptable.isPending}
        onFinish={(values) => mutationSaisirComptable.mutate(values)}
      />

      {/* Modal saisie règlement comptable par ligne */}
      <ModalSaisirRcLigne
        paiement={saisirRcRow}
        open={!!saisirRcRow}
        onCancel={() => setSaisirRcRow(null)}
        loading={mutationSaisirRcLigne.isPending}
        onFinish={(body) => {
          if (saisirRcRow) {
            mutationSaisirRcLigne.mutate({
              paiementTrackingId: saisirRcRow.paiementTrackingId,
              body,
            });
          }
        }}
      />
    </Layout>
  );
}

// Modal saisie règlement comptable par ligne de paiement
function ModalSaisirRcLigne({ paiement, open, onCancel, loading, onFinish }) {
  const { t } = useTranslation('reglementsExperts');
  const [form] = Form.useForm();
  const [banques, setBanques] = React.useState([]);

  React.useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      dateEmissionReglement: dayjs(),
      dateEmissionCheque: dayjs(),
    });
    axiosInstance.get('/v1/banques')
      .then((r) => setBanques(r.data?.data || []))
      .catch(() => { });
  }, [open, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onFinish({
        numeroChequeEmis: values.numeroChequeEmis,
        banqueCheque: values.banqueCheque,
        dateEmissionReglement: values.dateEmissionReglement.format('YYYY-MM-DD'),
        dateEmissionCheque: values.dateEmissionCheque.format('YYYY-MM-DD'),
      });
    } catch {
      // Validation handled by AntD Form
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BankOutlined style={{ color: '#1a6b3c', fontSize: 18 }} />
          <span style={{ fontWeight: 700 }}>
            {t('rc.titre', { numero: paiement?.numeroPaiement || '' })}
          </span>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okText={t('actions.enregistrer')}
      cancelText={t('actions.annuler')}
      width={540}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="numeroChequeEmis"
              label={t('rc.numeroCheque')}
              rules={[{ required: true, message: t('rc.numeroRequis') }]}
            >
              <Input placeholder={t('cheque.exempleNumero')} style={{ borderRadius: 8 }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="banqueCheque"
              label={t('rc.banque')}
              rules={[{ required: true, message: t('rc.banqueRequise') }]}
            >
              <Select
                showSearch
                options={banques.map((b) => ({
                  value: b.nom,
                  label: b.code ? `${b.nom} (${b.code})` : b.nom,
                }))}
                placeholder={banques.length === 0 ? t('cheque.aucuneBanque') : t('rc.selectionnerBanque')}
                disabled={banques.length === 0}
                filterOption={(input, opt) =>
                  String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="dateEmissionReglement"
              label={t('cheque.dateEmissionReglement')}
              rules={[{ required: true, message: t('rc.dateRequise') }]}
            >
              <DatePicker
                style={{ width: '100%', borderRadius: 8 }}
                format="DD/MM/YYYY"
                disabledDate={(d) => d && d > dayjs().endOf('day')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="dateEmissionCheque"
              label={t('cheque.dateEmissionCheque')}
              rules={[{ required: true, message: t('rc.dateRequise') }]}
            >
              <DatePicker
                style={{ width: '100%', borderRadius: 8 }}
                format="DD/MM/YYYY"
                disabledDate={(d) => d && d > dayjs().endOf('day')}
              />
            </Form.Item>
          </Col>
        </Row>
        {banques.length === 0 && (
          <Alert
            type="info"
            showIcon
            style={{ borderRadius: 8, marginTop: 4 }}
            message={t('cheque.aucuneBanqueAide')}
          />
        )}
      </Form>
    </Modal>
  );
}
